import React, { useMemo } from "react";
import { Alert, Box, CircularProgress, Typography, useTheme } from "@mui/material";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Generic daily bar chart.
 *
 * Props:
 *   labels        — x-axis labels (e.g. day numbers)
 *   data          — numeric values parallel to labels
 *   loading       — shows a spinner instead of the chart
 *   error         — shows an error alert above the chart
 *   height        — chart canvas height in px (default 84)
 *   color         — bar fill color (defaults to theme primary)
 *   datasetLabel  — dataset label for the legend / tooltip
 *   emptyMessage  — text shown when all values are 0
 *   tooltipTitle  — (context) => string  override for tooltip title
 *   tooltipLabel  — (context) => string  override for tooltip label
 */
const BarChartPlot = ({
  labels = [],
  data = [],
  loading = false,
  error = "",
  height = 84,
  color,
  datasetLabel = "",
  emptyMessage = "No data recorded for this period.",
  tooltipTitle,
  tooltipLabel,
  xAxisTitle,
  yAxisTitle,
}) => {
  const theme = useTheme();
  const barColor = color || theme.palette.primary.main;
  // Chart.js defaults (#666 labels, black 10% grid) assume a light background.
  const axisColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;
  const hasData = data.some((v) => v > 0);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: datasetLabel,
          data,
          backgroundColor: barColor,
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 6,
        },
      ],
    }),
    [labels, data, barColor, datasetLabel],
  );

  const chartOptions = useMemo(
    () => ({
      // Chart.js animates on every mount by default; full page reloads remount the canvas.
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 0, right: 4, top: 2, bottom: 0 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            ...(tooltipTitle ? { title: tooltipTitle } : {}),
            ...(tooltipLabel ? { label: tooltipLabel } : {}),
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: gridColor },
          border: { color: gridColor },
          ticks: {
            color: axisColor,
            precision: 0,
            stepSize: 1,
            font: { size: 9 },
            maxTicksLimit: 4,
          },
          ...(yAxisTitle
            ? { title: { display: true, text: yAxisTitle, color: axisColor, font: { size: 9 } } }
            : {}),
        },
        x: {
          border: { color: gridColor },
          ticks: {
            color: axisColor,
            autoSkip: labels.length > 16,
            maxTicksLimit: 16,
            font: { size: 9 },
          },
          grid: { display: false },
          ...(xAxisTitle
            ? { title: { display: true, text: xAxisTitle, color: axisColor, font: { size: 9 } } }
            : {}),
        },
      },
    }),
    [labels.length, tooltipTitle, tooltipLabel, xAxisTitle, yAxisTitle, axisColor, gridColor],
  );

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height, position: "relative" }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>
      {!hasData ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {emptyMessage}
        </Typography>
      ) : null}
    </>
  );
};

export default BarChartPlot;
